// src/components/AnalyticsCard.js
import React, { useMemo, useState } from "react";
import { FiXCircle } from "react-icons/fi";
import { useAnalytics } from "../hooks/useAnalytics";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { callOpenAI, OPENAI_MODELS } from "../api/openai";
import { playSystemSound } from "../audio/soundManager";
import {
  buildHunterProfileContext,
  buildMemoryContext,
  buildSystemPrompt,
} from "../utils/systemPrompts";
import { buildSystemMemory } from "../utils/systemMemory";
import { getDatePartsInTimeZone } from "../utils/timezoneUtils";

const formatAxisDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
};

const AnalyticsTooltip = ({ active, label, payload }) => {
  if (!active || !payload?.length) return null;

  const formatCount = (value, singular, plural) => {
    const numeric = Number(value || 0);
    return `${numeric} ${numeric === 1 ? singular : plural}`;
  };

  const formatValue = (entry) => {
    const value = Number(entry?.value ?? 0);
    if (entry?.dataKey === "xp") {
      if (value > 0) return `+${value} XP`;
      if (value < 0) return `${value} XP`;
      return "0 XP";
    }
    if (entry?.dataKey === "completions") {
      return formatCount(value, "quest completed", "quests completed");
    }
    if (entry?.dataKey === "penalties") {
      return formatCount(value, "penalty quest issued", "penalty quests issued");
    }
    return String(value);
  };

  return (
    <div className="analytics-tooltip">
      <div className="analytics-tooltip__label">{formatAxisDate(label)}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="analytics-tooltip__row">
          <span
            className="analytics-tooltip__swatch"
            style={{ backgroundColor: entry.color || entry.fill }}
          />
          <span className="analytics-tooltip__name">{entry.name}</span>
          <span className="analytics-tooltip__value">{formatValue(entry)}</span>
        </div>
      ))}
    </div>
  );
};

const AnalyticsCard = ({ user, userProfile }) => {
  const refreshKey = userProfile?.updatedAt || `${userProfile?.totalXP || 0}:${userProfile?.level || 1}`;
  const { analytics, loading } = useAnalytics(user?.uid, refreshKey, userProfile?.timeZone || null);
  const [analysing, setAnalysing] = useState(false);
  const [analysisText, setAnalysisText] = useState("");
  const [showModal, setShowModal] = useState(false);

  const xpData = analytics?.xpByDate || [];
  const compsData = analytics?.completionsByDate || [];
  const pensData = analytics?.penaltiesByDate || [];

  // Merge completions & penalties by date for the bar chart
  const compPenData = useMemo(() => {
    const map = new Map();
    for (const r of compsData) {
      map.set(r.date, { date: r.date, completions: r.count || r.value || 0, penalties: 0 });
    }
    for (const r of pensData) {
      const prev = map.get(r.date) || { date: r.date, completions: 0, penalties: 0 };
      prev.penalties = (r.count || r.value || 0);
      map.set(r.date, prev);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [compsData, pensData]);

  const assessment = useMemo(() => {
    const streak = Number(analytics?.streak || 0);
    const penaltyRate7d = Number(analytics?.penaltyRate7d || 0);
    const penaltiesLast7d = Number(analytics?.penaltiesLast7d || 0);
    const completedQuestsLast7d = Number(analytics?.completedQuestsLast7d || 0);
    const completionRate7d = Number(analytics?.completionRate7d || 0);
    const bossQuest = analytics?.currentWeekBossQuest;
    const weekday = getDatePartsInTimeZone(userProfile?.timeZone || "UTC").weekday;
    const isLateWeek = ["Thu", "Fri", "Sat", "Sun"].includes(weekday);
    const bossQuestLate =
      isLateWeek &&
      bossQuest &&
      bossQuest.claimed !== true &&
      bossQuest.status !== "completed";

    if (streak >= 7 && penaltiesLast7d === 0) {
      return {
        tone: "analytics-assessment--stable",
        line: "HUNTER STATUS: EXCEPTIONAL. Sustained performance above baseline. The System is observing.",
      };
    }

    if (streak >= 3 && penaltyRate7d < 20) {
      return {
        tone: "analytics-assessment--stable",
        line: "HUNTER STATUS: STABLE. Objectives are being met. Maintain current output.",
      };
    }

    if (penaltiesLast7d >= 3) {
      return {
        tone: "analytics-assessment--critical",
        line: "HUNTER STATUS: DEGRADED. Repeated failures logged. Performance intervention recommended.",
      };
    }

    if (streak === 0 && completedQuestsLast7d < 5) {
      return {
        tone: "analytics-assessment--critical",
        line: "HUNTER STATUS: CRITICAL. Activity has fallen below minimum thresholds. The System is not satisfied.",
      };
    }

    if (bossQuestLate) {
      return {
        tone: "analytics-assessment--warning",
        line: "WARNING. Weekly objective unresolved. Time remaining is limited.",
      };
    }

    return {
      tone: "analytics-assessment--stable",
      line: "HUNTER STATUS: ACTIVE. Performance within acceptable range. Continue.",
    };
  }, [analytics, userProfile?.timeZone]);

  const summaryStats = [
    {
      label: "Current perfect-day streak (30d)",
      value: `${analytics?.streak ?? 0} days`,
      tone: "analytics-stat--gold",
    },
    {
      label: "Best perfect-day streak (30d)",
      value: `${analytics?.bestStreak ?? 0} days`,
      tone: "analytics-stat--green",
    },
    {
      label: "Current total XP",
      value: analytics?.totalXP ?? 0,
      tone: "analytics-stat--blue",
    },
    {
      label: "Tracked quests (30d)",
      value: analytics?.totalQuests ?? 0,
      tone: "",
    },
    {
      label: "Penalty quests issued (30d)",
      value: analytics?.totalPenalties ?? 0,
      tone: "analytics-stat--red",
    },
  ];

  if (loading) return <div className="stats-card analytics-card analytics-card--loading">Loading analytics...</div>;

  const handleAnalyse = async () => {
    try {
      setAnalysing(true);
      setAnalysisText("");
      playSystemSound("questAction");
      // Build a compact, privacy-safe summary for the model
      const payload = {
        xpByDate: xpData.slice(-30),
        completionsByDate: compsData.slice(-30),
        penaltiesByDate: pensData.slice(-30),
        totals: {
          streak: analytics?.streak ?? 0,
          bestStreak: analytics?.bestStreak ?? 0,
          totalXP: analytics?.totalXP ?? 0,
          totalQuests: analytics?.totalQuests ?? 0,
          totalPenalties: analytics?.totalPenalties ?? 0,
        },
      };

      const memory = await buildSystemMemory(user?.uid);

      const prompt = buildSystemPrompt({
        contextLines: [
          "Analyse the hunter's recent progress data.",
          `Progress payload JSON: ${JSON.stringify(payload)}`,
          buildHunterProfileContext(userProfile),
          buildMemoryContext(memory),
        ],
        instructionLines: [
          "Summarize trends across XP, streaks, completions, and penalties.",
          "Use the hunter record to make the assessment feel historically aware.",
          "Reference long-term highs, failures, abandoned bosses, or repeated breaks when relevant.",
          "Do not mechanically dump every memory statistic.",
          "Recommend concrete next-step actions for the coming week.",
          "Suggest one weekly boss quest title and one-sentence objective.",
        ],
        outputLines: [
          "Use bracketed headers and short lines.",
          "Structure the response as [PROGRESS REPORT], [RECOMMENDATIONS], and [BOSS QUEST SUGGESTION].",
          "110 to 220 words total.",
          "No markdown, no code fences.",
          "Do not invent exact dates; speak in general trends.",
        ],
      });

      const out = await callOpenAI(prompt, 360, 0.7, { model: OPENAI_MODELS.ANALYSIS });
      const text = (out?.result || "").trim();
      setAnalysisText(text || "[No analysis generated.]");
      playSystemSound("questOpen");
      setShowModal(true);
    } catch (e) {
      console.error(e);
      setAnalysisText("[System error] Unable to analyse progress right now.");
      playSystemSound("questOpen");
      setShowModal(true);
    } finally {
      setAnalysing(false);
    }
  };

  return (
    <div className="stats-card analytics-card">
      <h4>Progress Analytics</h4>

      <section className={`analytics-assessment ${assessment.tone}`.trim()}>
        <div className="analytics-assessment__label">[SYSTEM ASSESSMENT]</div>
        <div className="analytics-assessment__line">{assessment.line}</div>
        <div className="analytics-assessment__chips">
          <span className="analytics-assessment__chip">
            STREAK: {analytics?.streak ?? 0} DAYS
          </span>
          <span className="analytics-assessment__chip">
            7D COMPLETION: {analytics?.completionRate7d ?? 0}%
          </span>
        </div>
      </section>

      <div className="analytics-card__toolbar">
        <button
          className="login-btn analytics-card__toolbar-btn"
          type="button"
          onClick={handleAnalyse}
          disabled={analysing}
        >
          {analysing ? "Analysing…" : "Let system analyse the progress"}
        </button>
      </div>

      <div className="analytics-card__charts">
        <section className="analytics-panel">
          <div className="analytics-panel__header">
            <h5>Daily net XP</h5>
            <span>Last 30 days</span>
          </div>
          <div className="analytics-panel__chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={xpData} margin={{ top: 10, right: 10, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(58,226,255,0.16)" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#9ecbff", fontSize: 12 }}
                  tickFormatter={formatAxisDate}
                  minTickGap={22}
                  stroke="rgba(66,170,255,.2)"
                />
                <YAxis
                  tick={{ fill: "#9ecbff", fontSize: 12 }}
                  width={52}
                  tickMargin={8}
                  allowDecimals={false}
                  stroke="rgba(66,170,255,.2)"
                />
                <Tooltip content={<AnalyticsTooltip />} />
                <Line
                  type="monotone"
                  dataKey="xp"
                  name="Net XP"
                  stroke="#21eaff"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 4, fill: "#21eaff", stroke: "#0c1220", strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="analytics-panel analytics-panel--bars">
          <div className="analytics-panel__header">
            <h5>Completed quests vs penalty quests issued</h5>
            <span>Last 30 days</span>
          </div>
          <div className="analytics-panel__chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={compPenData} margin={{ top: 10, right: 10, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(58,226,255,0.16)" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#9ecbff", fontSize: 12 }}
                  tickFormatter={formatAxisDate}
                  minTickGap={22}
                  stroke="rgba(66,170,255,.2)"
                />
                <YAxis
                  tick={{ fill: "#9ecbff", fontSize: 12 }}
                  width={52}
                  tickMargin={8}
                  allowDecimals={false}
                  stroke="rgba(66,170,255,.2)"
                />
                <Tooltip content={<AnalyticsTooltip />} />
                <Bar
                  dataKey="completions"
                  name="Completed quests"
                  fill="#4fff91"
                  radius={[6, 6, 0, 0]}
                />
                <Bar
                  dataKey="penalties"
                  name="Penalty quests issued"
                  fill="#ff4b70"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="analytics-stats">
        {summaryStats.map((item) => (
          <div key={item.label} className={`analytics-stat ${item.tone}`.trim()}>
            <span className="analytics-stat__label">{item.label}</span>
            <span className="analytics-stat__value">{item.value}</span>
          </div>
        ))}
      </div>

      {/* Solo Leveling–style popup */}
      {showModal && (
        <div className="solo-modal-bg" onClick={() => setShowModal(false)}>
          <div className="solo-modal analytics-modal" onClick={(e) => e.stopPropagation()}
          >
            <button
              className="solo-close"
              type="button"
              onClick={() => setShowModal(false)}
              title="Close"
            >
              <FiXCircle />
            </button>

            <div className="analytics-modal__header">
              <h2>Notification</h2>
            </div>

            <div className="analytics-modal__body">
              <div className="quest-text analytics-modal__text">
                {analysisText.split("\n").map((line, i) => (
                  <div key={i}>{line || "\u00A0"}</div>
                ))}
              </div>
            </div>

            <div className="solo-actions analytics-modal__actions">
              <button
                className="login-btn analytics-modal__action-btn"
                type="button"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsCard;
